import { createSignal } from 'solid-js';
import type { Command, CommandRegistryInterface } from './types';
import { CommandError } from './types';

/**
 * CommandRegistry implementation with SolidJS reactive patterns
 *
 * Provides a registry for managing commands with reactive updates
 * compatible with SolidJS applications
 */
export function createCommandRegistry(): CommandRegistryInterface {
	const [commands, setCommands] = createSignal<Map<string, Command>>(new Map());

	const register = (command: Command): void => {
		if (!command.id?.trim()) {
			throw new Error(CommandError.INVALID_COMMAND);
		}

		if (!command.title?.trim()) {
			throw new Error(CommandError.INVALID_COMMAND);
		}

		if (typeof command.action !== 'function') {
			throw new Error(CommandError.INVALID_COMMAND);
		}

		setCommands((prev) => {
			if (prev.has(command.id)) {
				throw new Error(CommandError.DUPLICATE_ID);
			}

			const newCommands = new Map(prev);
			newCommands.set(command.id, command);
			return newCommands;
		});
	};

	const unregister = (commandId: string): void => {
		setCommands((prev) => {
			if (!prev.has(commandId)) {
				throw new Error(CommandError.COMMAND_NOT_FOUND);
			}

			const newCommands = new Map(prev);
			newCommands.delete(commandId);
			return newCommands;
		});
	};

	const getAll = (): Command[] => {
		return Array.from(commands().values());
	};

	const getById = (commandId: string): Command | undefined => {
		return commands().get(commandId);
	};

	const search = (query: string): Command[] => {
		if (!query?.trim()) {
			return getAll();
		}

		const lowerQuery = query.toLowerCase().trim();
		return getAll().filter((command) => {
			const titleMatch = command.title.toLowerCase().includes(lowerQuery);
			const descriptionMatch = command.description?.toLowerCase().includes(lowerQuery) ?? false;
			return titleMatch || descriptionMatch;
		});
	};

	return {
		register,
		unregister,
		getAll,
		getById,
		search,
	};
}
